// =============================================================================
// diaryPhotoUpload.ts — T-258. Multi-photo upload helper for Site Diary.
//
// Adapts the hopUpload.ts pattern for the simpler site diary case:
//   1. Parse EXIF GPS + DateTimeOriginal client-side (no watermark burn-in).
//   2. Get a presigned R2 PUT URL from /diary/sign-upload.
//   3. PUT the bytes directly to R2 (zero data through the Worker).
//   4. Insert a site_diary_photos row via supabase (RLS gates visibility).
//
// Why no Worker `/diary/finalise-photo` endpoint? Unlike HOP assets which
// have complex categorisation + label_prefix watermarking, diary photos are
// simple — a single child table insert with no derived fields. RLS on
// site_diary_photos already validates project visibility + that the entry
// exists. Saves a round-trip + ~50 lines of Worker code.
//
// Path: diary/<project_id>/<diary_id>/photo_<ts>-<sort>.<ext>  (R2 key)
// Bucket: coretel-prod (same as HOPs / deliverables / single-photo diary).
//
// Storage key shape preserves chronology even when sort_order changes — the
// timestamp + sort index in the filename gives a natural sort fallback if
// the DB sort_order ever gets out of sync.
// =============================================================================

import { apiFetch, supabase } from "@/lib/supabase";
import { watermarkPhoto } from "@/lib/watermark";
import { fetchProjectAddressLines } from "@/lib/siteAddress";

// -----------------------------------------------------------------------------
// EXIF — first-class for library uploads, optional override for in-app camera
// -----------------------------------------------------------------------------
type GpsSource = "exif" | "device_current";

interface GpsResult {
  lat: number;
  lng: number;
  source: GpsSource;
  takenAtIso: string | null;
}

async function extractExifGps(file: File): Promise<{ lat: number; lng: number; takenAtIso: string | null } | null> {
  try {
    const exifr = (await import("exifr")).default ?? (await import("exifr"));
    const result = await (exifr as any).parse(file, {
      pick: ["GPSLatitude", "GPSLongitude", "GPSLatitudeRef", "GPSLongitudeRef", "DateTimeOriginal"],
      gps: true,
    });
    if (!result) return null;
    const lat = typeof result.latitude === "number" ? result.latitude : null;
    const lng = typeof result.longitude === "number" ? result.longitude : null;
    if (lat == null || lng == null) return null;
    if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    let takenAtIso: string | null = null;
    if (result.DateTimeOriginal instanceof Date && !isNaN(result.DateTimeOriginal.getTime())) {
      takenAtIso = result.DateTimeOriginal.toISOString();
    }
    return { lat, lng, takenAtIso };
  } catch (err) {
    console.debug("[diaryPhotoUpload] EXIF parse failed", err);
    return null;
  }
}

async function resolveGps(
  file: File,
  precomputedGps?: { lat: number; lng: number } | null,
): Promise<GpsResult | null> {
  // In-app camera capture: caller passes live coords. Authoritative.
  if (precomputedGps !== undefined && precomputedGps !== null) {
    return { lat: precomputedGps.lat, lng: precomputedGps.lng, source: "device_current", takenAtIso: null };
  }
  // Library upload: EXIF only. T-217 rule — never fall back to device location
  // or upload time. Missing data → blank, never wrong.
  const exif = await extractExifGps(file);
  if (exif) {
    return { lat: exif.lat, lng: exif.lng, source: "exif", takenAtIso: exif.takenAtIso };
  }
  return null;
}

// -----------------------------------------------------------------------------
// Upload one photo
// -----------------------------------------------------------------------------

export interface DiaryPhotoUploadArgs {
  diaryId:        string;       // pre-generated uuid (entry must already exist)
  projectId:      string;
  file:           File;
  caption?:       string | null;
  kind?:          string;        // T-329 photo category ('general'|'setup'|'works'|'delay'|'plant')
  sortOrder:      number;
  /** Optional pre-computed GPS for in-app camera captures */
  precomputedGps?: { lat: number; lng: number } | null;
  /** T-366 — when true, skip the burned watermark and upload the original
   *  bytes as-is (GPS/taken_at metadata is still resolved + stored). Default
   *  behaviour (undefined/false) burns the watermark, same as before. */
  skipWatermark?: boolean;
  onProgress?:    (phase: "sign" | "upload" | "save", pct: number) => void;
}

export interface DiaryPhotoUploadResult {
  photo_id: string;
  r2_key:   string;
}

export async function uploadDiaryPhoto(args: DiaryPhotoUploadArgs): Promise<DiaryPhotoUploadResult> {
  const { diaryId, projectId, file, caption, sortOrder, onProgress } = args;

  // 1. Resolve GPS + capture time from EXIF (or precomputed for live camera)
  const resolved = await resolveGps(file, args.precomputedGps);
  const gps: { lat: number; lng: number } | null = resolved ? { lat: resolved.lat, lng: resolved.lng } : null;
  const gpsSource: GpsSource | null = resolved?.source ?? null;
  let takenAt: string | null = null;
  if (resolved) {
    if (resolved.source === "device_current") {
      takenAt = new Date().toISOString();  // in-app capture = live time
    } else {
      takenAt = resolved.takenAtIso ?? null;  // EXIF date OR null
    }
  }

  // 2. T-260 — burn TimestampCamera-style watermark into the JPEG and re-
  // inject EXIF DateTimeOriginal + GPS via piexifjs. Same data-source rule
  // as HOP: takenAt + gps come from EXIF (library) or precomputedGps + now
  // (in-app camera). Missing data → omit the line, never fall back to
  // upload time or device-now. Non-image files (defensive — diary UI only
  // accepts images today) pass through untouched.
  let blobToUpload: Blob = file;
  let finalMime = file.type || "application/octet-stream";
  let finalSize = file.size;
  if (!args.skipWatermark && file.type.startsWith("image/")) {
    // T-260 Commit 2 — registered site address lines (offline, no reverse-
    // geocode). Burned below the date/GPS lines.
    const addressLines = await fetchProjectAddressLines(projectId);
    const wm = await watermarkPhoto(file, {
      takenAt: takenAt ? new Date(takenAt) : null,
      gps,
      addressLines,
    });
    blobToUpload = wm.blob;
    finalMime    = wm.mimeType;
    finalSize    = wm.blob.size;
  }

  // 3. Get presigned R2 PUT URL from existing /diary/sign-upload endpoint
  onProgress?.("sign", 0);
  const signRes = await apiFetch("/diary/sign-upload", {
    method: "POST",
    body: JSON.stringify({
      project_id:   projectId,
      diary_id:     diaryId,
      file_name:    file.name,
      content_type: finalMime,
    }),
  });
  if (!signRes.ok) {
    const err = await signRes.json().catch(() => ({}));
    throw new Error(`sign-upload failed (${signRes.status}): ${err.error ?? "unknown"}`);
  }
  const { upload_url, object_key } = await signRes.json() as { upload_url: string; object_key: string };
  onProgress?.("sign", 100);

  // 4. PUT bytes directly to R2
  onProgress?.("upload", 0);
  const putRes = await fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": finalMime },
    body:    blobToUpload,
  });
  if (!putRes.ok) {
    throw new Error(`R2 upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  onProgress?.("upload", 100);

  // 4. Insert site_diary_photos row via supabase. RLS validates that the
  //    parent entry exists AND the caller can see the project.
  onProgress?.("save", 0);
  // Need the internal user_id for uploaded_by_user_id (RLS check column).
  // Look up via auth.uid() → users.user_id.
  const { data: authUser } = await supabase.auth.getUser();
  if (!authUser?.user) throw new Error("not authenticated");
  const { data: userRow, error: userErr } = await supabase
    .from("users")
    .select("user_id")
    .eq("auth_user_id", authUser.user.id)
    .maybeSingle();
  if (userErr) throw userErr;
  if (!userRow) throw new Error("no internal user row for current auth");

  const { data, error } = await supabase
    .from("site_diary_photos")
    .insert({
      diary_id:         diaryId,
      r2_key:           object_key,
      mime_type:        finalMime,
      file_size_bytes:  finalSize,
      caption:          caption ?? null,
      photo_kind:       args.kind ?? "general",
      sort_order:       sortOrder,
      taken_at:         takenAt,
      gps_lat:          gps?.lat ?? null,
      gps_lng:          gps?.lng ?? null,
      gps_source:       gpsSource,
      uploaded_by_user_id: userRow.user_id,
    })
    .select("photo_id")
    .single();
  if (error) {
    // R2 object is now an orphan — cron sweep cleans it up (same as HOP)
    throw new Error(`save photo row failed: ${error.message}`);
  }
  onProgress?.("save", 100);

  return { photo_id: data.photo_id, r2_key: object_key };
}

// -----------------------------------------------------------------------------
// Download / view URLs
// -----------------------------------------------------------------------------

export async function getDiaryPhotoSignedUrl(r2Key: string, ttlSeconds: number = 1800): Promise<string | null> {
  try {
    const res = await apiFetch("/diary/sign-download", {
      method: "POST",
      body: JSON.stringify({ storage_path: r2Key, ttl_seconds: ttlSeconds }),
    });
    if (!res.ok) return null;
    const { download_url } = await res.json();
    return download_url ?? null;
  } catch {
    return null;
  }
}

/** Batch fan-out — N parallel sign requests. CF edge handles the load fine. */
export async function getDiaryPhotoSignedUrls(
  r2Keys: string[],
  ttlSeconds: number = 1800,
): Promise<Map<string, string>> {
  if (r2Keys.length === 0) return new Map();
  const results = await Promise.all(
    r2Keys.map(async (k) => [k, await getDiaryPhotoSignedUrl(k, ttlSeconds)] as const),
  );
  const map = new Map<string, string>();
  for (const [k, url] of results) {
    if (url) map.set(k, url);
  }
  return map;
}

// -----------------------------------------------------------------------------
// Delete (RLS: only uploader within 24h OR manager+)
// -----------------------------------------------------------------------------

export async function deleteDiaryPhoto(photoId: string): Promise<void> {
  const { error } = await supabase
    .from("site_diary_photos")
    .delete()
    .eq("photo_id", photoId);
  if (error) throw error;
}

// -----------------------------------------------------------------------------
// Reorder (manager+ or uploader within 24h)
// -----------------------------------------------------------------------------

export async function updateDiaryPhotoOrder(updates: { photo_id: string; sort_order: number }[]): Promise<void> {
  // No bulk-update RPC — fan out one update per row. Small payloads, RLS
  // does the work. Could optimize with an RPC if this becomes hot.
  for (const u of updates) {
    const { error } = await supabase
      .from("site_diary_photos")
      .update({ sort_order: u.sort_order })
      .eq("photo_id", u.photo_id);
    if (error) throw error;
  }
}

export async function updateDiaryPhotoCaption(photoId: string, caption: string | null): Promise<void> {
  const { error } = await supabase
    .from("site_diary_photos")
    .update({ caption: caption?.trim() || null })
    .eq("photo_id", photoId);
  if (error) throw error;
}
